<script lang="ts">
  import { api } from '../../lib/api/client';
  import BarChart from '../../lib/components/health/BarChart.svelte';
  import type { TravelMode } from '../../lib/api/types';
  import { integer, locale, t, time, weekday } from '../../lib/i18n/index.ts';
  import { distance, hourName, localDate, modeIcon, shortDate, span } from './format';

  // Commutes lists trips repeated from one of the member's places straight to
  // another, and breaks one down by the hour and weekday it started and by
  // month (spec 005). It only looks back; nothing here notifies.
  let { onDay }: { onDay: (date: string) => void } = $props();

  type Place = { id: string; name: string; category: string };
  type Commute = {
    from_place: Place;
    to_place: Place;
    trips: number;
    median_s: number;
    p10_s: number;
    p90_s: number;
    median_distance_m: number;
    modes: { mode: TravelMode; trips: number }[];
  };
  type Slot = { hour?: number; weekday?: number; month?: string; trips: number; median_s: number };
  type Detail = Commute & {
    hours: Slot[];
    weekdays: Slot[];
    months: Slot[];
    quickest_hour?: number;
    slowest_hour?: number;
    list: { start: string; end: string; date: string; duration_s: number; distance_m: number; mode: TravelMode }[];
  };

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let months = $state(3);
  let commutes = $state<Commute[] | null>(null);
  let open = $state<Commute | null>(null);
  let detail = $state<Detail | null>(null);
  let error = $state('');

  function range(): string {
    if (months === 0) return '';
    const from = new Date();
    from.setMonth(from.getMonth() - months);
    return `&from=${localDate(from)}&to=${localDate(new Date())}`;
  }

  $effect(() => {
    const q = `tz=${encodeURIComponent(tz)}${range()}`;
    open = null;
    api.get<Commute[]>(`/me/commutes?${q}`)
      .then((got) => { commutes = got; error = ''; })
      .catch((e) => { error = e.message; commutes = null; });
  });

  $effect(() => {
    const c = open;
    detail = null;
    if (!c) return;
    const q = `tz=${encodeURIComponent(tz)}&from_place=${c.from_place.id}&to_place=${c.to_place.id}${range()}`;
    api.get<Detail>(`/me/commutes/detail?${q}`).then((d) => (detail = d)).catch((e) => (error = e.message));
  });

  // dayName is the server's weekday, 1 for Monday to 7 for Sunday; 1 January
  // 2024 was a Monday.
  const dayName = (w: number, short = false) => weekday(new Date(2024, 0, w, 12), short);
  const monthLabel = (m: string) => new Date(`${m}-15T12:00:00`).toLocaleDateString(locale(), { month: 'short', year: '2-digit' });
  const minutes = (s: number) => t('timelineMinutes', { n: Math.round(s / 60) });
  const slot = (name: string, trips: number) => t('timelineCommuteSlot', { slot: name, trips });
  const modeList = (c: Commute) => new Intl.ListFormat(locale(), { type: 'unit', style: 'short' })
    .format(c.modes.map((m) => t('timelineCommuteModeTrips', { mode: m.mode, trips: integer(m.trips) })));
  const heading = { name: t('timelineMedian'), value: t('timelineMedian') };
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    <div class="flex flex-wrap items-center gap-2">
      <select class="input w-44" bind:value={months} aria-label={t('timelinePeriod')}>
        <option value={3}>{t('timelineLastMonths', { n: 3 })}</option>
        <option value={12}>{t('timelineLastMonths', { n: 12 })}</option>
        <option value={0}>{t('timelineAllTime')}</option>
      </select>
      <p class="text-sm text-text-muted">{t('timelineCommutesAbout')}</p>
    </div>
    {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}
    {#if commutes && commutes.length === 0}
      <p class="text-sm text-text-muted">{t('timelineCommutesNone')}</p>
    {/if}
    {#if commutes && !open}
      <ul class="card divide-y">
        {#each commutes as c (`${c.from_place.id}>${c.to_place.id}`)}
          <li>
            <button class="w-full text-left p-4 flex items-center gap-3 hover:opacity-80" onclick={() => (open = c)}>
              <i aria-hidden="true" class="las {modeIcon(c.modes[0]?.mode)} text-xl"></i>
              <span class="flex-1">
                <span class="font-medium">{t('timelineCommuteRoute', { from: c.from_place.name, to: c.to_place.name })}</span>
                <span class="block text-sm text-text-muted">
                  {t('timelineCommuteSummary', { trips: c.trips, median: span(c.median_s), fast: span(c.p10_s), slow: span(c.p90_s), distance: distance(c.median_distance_m) })}
                </span>
              </span>
              <i aria-hidden="true" class="las la-angle-right"></i>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
    {#if open}
      <button class="btn btn-secondary btn-sm" onclick={() => (open = null)}><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('timelineTabCommutes')}</button>
      <section class="card p-5 space-y-4">
        <div>
          <h2 class="font-semibold">{t('timelineCommuteRoute', { from: open.from_place.name, to: open.to_place.name })}</h2>
          <p class="text-sm text-text-muted">
            {t('timelineCommuteDetail', { trips: open.trips, median: span(open.median_s), modes: modeList(open) })}
          </p>
        </div>
        {#if detail}
          {#if detail.quickest_hour != null && detail.slowest_hour != null}
            <p class="text-sm">
              {t('timelineCommuteQuickest', { quickest: hourName(detail.quickest_hour), slowest: hourName(detail.slowest_hour) })}
            </p>
          {/if}
          <div>
            <h3 class="text-sm font-medium mb-1">{t('timelineCommuteByHour')}</h3>
            <BarChart values={detail.hours.map((h) => h.median_s / 60)} labels={detail.hours.map((h) => hourName(h.hour!))}
              names={detail.hours.map((h) => slot(hourName(h.hour!), h.trips))}
              color="var(--theme-blue)" caption={t('timelineCommuteHourCaption')} format={(v) => t('timelineMinutes', { n: Math.round(v) })}
              {heading} unit={t('timelineMinUnit')} />
          </div>
          <div>
            <h3 class="text-sm font-medium mb-1">{t('timelineCommuteByWeekday')}</h3>
            <BarChart values={detail.weekdays.map((w) => w.median_s / 60)} labels={detail.weekdays.map((w) => dayName(w.weekday!, true))}
              names={detail.weekdays.map((w) => slot(dayName(w.weekday!), w.trips))}
              color="var(--theme-green)" caption={t('timelineCommuteWeekdayCaption')} format={(v) => t('timelineMinutes', { n: Math.round(v) })}
              {heading} unit={t('timelineMinUnit')} />
          </div>
          {#if detail.months.length > 1}
            <div>
              <h3 class="text-sm font-medium mb-1">{t('timelineCommuteByMonth')}</h3>
              <BarChart values={detail.months.map((m) => m.median_s / 60)} labels={detail.months.map((m) => monthLabel(m.month!))}
                names={detail.months.map((m) => slot(monthLabel(m.month!), m.trips))}
                color="var(--theme-mauve)" caption={t('timelineCommuteMonthCaption')} format={(v) => t('timelineMinutes', { n: Math.round(v) })}
                {heading} unit={t('timelineMinUnit')} />
            </div>
          {/if}
          <div>
            <h3 class="text-sm font-medium mb-1">{t('timelineCommuteTrips')}</h3>
            <ul class="divide-y text-sm">
              {#each detail.list as trip (trip.start)}
                <li>
                  <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80" onclick={() => onDay(trip.date)}>
                    <i aria-hidden="true" class="las {modeIcon(trip.mode)}"></i>
                    <span class="flex-1">{t('timelineCommuteLeft', { date: shortDate(trip.start), time: time(new Date(trip.start)) })}</span>
                    <span class="tabular-nums">{minutes(trip.duration_s)}</span>
                  </button>
                </li>
              {/each}
            </ul>
          </div>
        {/if}
      </section>
    {/if}
  </div>
</div>
